import { FC, useState } from "react";
import { Pagination } from "@mantine/core";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { usePage } from "hooks/usePage";
import PageHeader from "components/layout/page-header";
import PageWrapper from "components/layout/page-wrapper";
import ClaimsGrid from "./components/ClaimsGrid";
import ClaimsFilter from "./components/ClaimsFilter";
import { useClaims } from "hooks/useClaims";

const ClaimsPage: FC = () => {
    const { t } = useTranslation();
    usePage("Kava | My Claims");

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("All");
    const [filters, setFilters] = useState({
        offerId: "",
        dateFrom: "",
        dateTo: "",
    });

    useDebounce(
        () => {
            setDebouncedSearchValue(searchValue);
        },
        1000,
        [searchValue],
    );

    const { claims, loading, error, refresh } = useClaims({
        limit: perPage,
        offset: (page - 1) * perPage,
        offerId: filters.offerId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
    });

    const filteredClaims = claims
        .filter(
            (claim): claim is NonNullable<typeof claim> =>
                claim !== null && claim !== undefined,
        )
        .filter((claim) => {
            const matchesSearch =
                !debouncedSearchValue ||
                claim.offer_claim_id
                    ?.toLowerCase()
                    .includes(debouncedSearchValue.toLowerCase()) ||
                claim.offer_id
                    ?.toLowerCase()
                    .includes(debouncedSearchValue.toLowerCase());

            return matchesSearch;
        });

    const totalClaims = filteredClaims.length;
    const from = totalClaims === 0 ? 0 : (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalClaims);
    const lastPage = Math.ceil(totalClaims / perPage);

    const handleStatusFilterChange = (status: string) => {
        setStatusFilter(status);
        setPage(1);
    };

    const handleFilterChange = (newFilters: {
        offerId: string;
        dateFrom: string;
        dateTo: string;
    }) => {
        setFilters(newFilters);
        setPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            offerId: "",
            dateFrom: "",
            dateTo: "",
        });
        setPage(1);
    };

    return (
        <PageWrapper>
            <PageHeader title={t("pages.claims.title")} />

            <section className="mt-8" aria-labelledby="claims-heading">
                <ClaimsFilter
                    offerId={filters.offerId}
                    dateFrom={filters.dateFrom}
                    dateTo={filters.dateTo}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                />

                <ClaimsGrid
                    claims={filteredClaims as any}
                    searchValue={searchValue}
                    search={setSearchValue}
                    loading={loading}
                    error={error}
                    refresh={refresh}
                    statusFilter={statusFilter}
                    onStatusFilterChange={handleStatusFilterChange}
                />
            </section>

            <section className="p-3 flex justify-between items-center mt-6">
                <span>
                    {t("pages.all_business.pagination.showing", {
                        from,
                        to,
                        total: totalClaims,
                    })}
                </span>
                {lastPage > 1 && (
                    <Pagination
                        total={lastPage}
                        page={page}
                        onChange={(page) => {
                            setPage(page);
                        }}
                    />
                )}
            </section>
        </PageWrapper>
    );
};

export default ClaimsPage;
